import { useTranslation } from 'react-i18next';
import {
  Archive,
  Ban,
  CheckCircle2,
  CircleOff,
  FileDown,
  Inbox,
  Send,
  Settings2,
  Shield,
  ShieldAlert,
  Wrench,
} from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/ui';
import { formatDate, formatDateTime } from '@shared/lib/format-date';
import { useCopyToClipboard } from '@shared/lib/use-copy-to-clipboard';
import { CopyFeedbackIcon } from '@shared/ui/copy-feedback-icon';
import type { WalletMonitoringAddress } from '@shared/api/contracts';
import {
  formatWalletBalanceLines,
  formatWalletMoney,
  getNetworkDotColor,
  isAttentionRisk,
  shortenAddress,
  type WalletAddressStatus,
  type WalletRiskLevel,
} from '@entities/wallets';
import { cn } from '@shared/lib/utils';

interface AddressRowProps {
  address: WalletMonitoringAddress;
  onOpenControlMode: () => void;
  onToggleAml: () => void;
  isAmlPending?: boolean;
  isControlModePending?: boolean;
}

const RISK_BADGE: Record<WalletRiskLevel, string> = {
  low: 'bg-emerald-500/15 text-emerald-700',
  medium: 'bg-yellow-500/15 text-yellow-700',
  high: 'bg-orange-500/15 text-orange-700',
  critical: 'bg-red-500/15 text-red-700',
  unknown: 'bg-secondary text-muted-foreground',
};

const RISK_DOT: Record<WalletRiskLevel, string> = {
  low: 'bg-emerald-600',
  medium: 'bg-yellow-500',
  high: 'bg-orange-500',
  critical: 'bg-red-600',
  unknown: 'bg-gray-400',
};

const STATUS_BADGE: Record<WalletAddressStatus, string> = {
  active: 'bg-emerald-500/15 text-emerald-700',
  disabled: 'bg-gray-100 text-gray-600',
  maintenance: 'bg-yellow-500/15 text-yellow-800',
  blocked: 'bg-red-500/15 text-red-700',
  receive_only: 'bg-orange-500/15 text-orange-700',
  withdraw_only: 'bg-blue-500/15 text-blue-700',
  archived: 'bg-gray-200 text-gray-800',
};

const STATUS_ICON = {
  active: CheckCircle2,
  disabled: CircleOff,
  maintenance: Wrench,
  blocked: Ban,
  receive_only: Inbox,
  withdraw_only: Send,
  archived: Archive,
} as const;

export function AddressRow({
  address,
  onOpenControlMode,
  onToggleAml,
  isAmlPending = false,
  isControlModePending = false,
}: AddressRowProps) {
  const { t } = useTranslation('cabinet');
  const { copy, isCopied } = useCopyToClipboard();
  const addressCopyKey = `address-${address.id}`;
  const StatusIcon = STATUS_ICON[address.status];
  const showRiskShield = isAttentionRisk(address.aml.riskLevel);
  const isActionPending = isAmlPending || isControlModePending;

  const handleRiskClick = () => {
    if (address.aml.pdfUrl) {
      window.open(address.aml.pdfUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const hasFact = address.factAmount != null;
  const factLabel = hasFact
    ? formatWalletMoney(address.factAmount, { asset: address.factAsset })
    : t('wallets.balance.noValuation');

  const balanceLines = formatWalletBalanceLines(address.balances);
  const currencyCodes = address.currencies.map((currency) => currency.shortCode).join(', ');
  const checkedAt = address.aml.checkedAt ?? null;

  return (
    <tr className="border-b border-border/60 align-middle last:border-0">
      <td className="px-3 py-2">
        <span className="inline-flex items-center gap-1.5 font-mono text-[12.5px]">
          <span className="break-all" title={address.address}>{shortenAddress(address.address)}</span>
          <button
            type="button"
            className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors duration-fast ease-standard hover:bg-muted/60 hover:text-foreground"
            onClick={() => void copy(address.address, undefined, addressCopyKey)}
            aria-label={t('common.copy', { ns: 'common' })}
          >
            <CopyFeedbackIcon copied={isCopied(addressCopyKey)} className="h-3 w-3" />
          </button>
        </span>
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          {address.network.iconUrl ? (
            <img src={address.network.iconUrl} alt="" className="size-3.5 rounded-full object-cover" />
          ) : (
            <span
              className="inline-block size-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: getNetworkDotColor(address.network.code) }}
            />
          )}
          <span>{address.network.code}</span>
          {currencyCodes ? <span className="text-[11px]">({currencyCodes})</span> : null}
        </span>
      </td>
      <td className="px-3 py-2 text-[11.5px] tabular-nums text-muted-foreground">
        {balanceLines.length ? (
          <div className="flex flex-col gap-0.5">
            {balanceLines.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </div>
        ) : (
          '—'
        )}
      </td>
      <td
        className={cn(
          'w-[1%] whitespace-nowrap px-2 py-2 text-right tabular-nums',
          hasFact
            ? 'font-semibold'
            : 'text-[11px] font-normal text-muted-foreground',
        )}
      >
        {factLabel}
      </td>
      <td className="px-3 py-2">
        <button
          type="button"
          onClick={handleRiskClick}
          disabled={!address.aml.pdfUrl}
          className={cn(
            'inline-flex items-center gap-1 rounded-md px-1.5 py-1 transition-colors',
            address.aml.pdfUrl ? 'hover:bg-muted/60' : 'cursor-default opacity-80',
          )}
          title={
            address.aml.pdfUrl
              ? `${t(`wallets.risk.${address.aml.riskLevel}`)} · ${t('wallets.actions.downloadPdf')}`
              : t(`wallets.risk.${address.aml.riskLevel}`)
          }
        >
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums',
              RISK_BADGE[address.aml.riskLevel],
            )}
          >
            <span className={`inline-block size-1.5 rounded-full ${RISK_DOT[address.aml.riskLevel]}`} />
            {address.aml.riskScore == null ? '—' : address.aml.riskScore}
            {showRiskShield ? <ShieldAlert className="h-2.5 w-2.5" /> : null}
          </span>
        </button>
        <div className="text-[10px] text-muted-foreground">{t(`wallets.aml.state.${address.aml.state}`)}</div>
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-[11.5px] text-muted-foreground">
        {checkedAt ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default">{formatDate(checkedAt)}</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{formatDateTime(checkedAt, 'dd.MM.yyyy, HH:mm:ss')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          '—'
        )}
      </td>
      <td className="px-3 py-2">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  'inline-flex size-[26px] items-center justify-center rounded-full',
                  STATUS_BADGE[address.status],
                )}
              >
                <StatusIcon className="h-3.5 w-3.5" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t(`wallets.status.${address.status}`)}</p>
              <p className="text-xs text-muted-foreground">
                {t('wallets.actions.statusSource', {
                  source: t(`wallets.statusSource.${address.statusSource}`),
                })}
              </p>
              {!address.availableForNewInvoice && (
                <p className="text-xs text-muted-foreground">
                  {t('wallets.actions.invoiceUnavailable')}
                </p>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </td>
      <td className="px-3 py-2">
        <span
          className={cn(
            'inline-flex rounded-full px-2 py-0.5 text-[10.5px] font-semibold',
            address.inRotation
              ? 'bg-emerald-500/15 text-emerald-700'
              : 'bg-gray-100 text-gray-600',
          )}
        >
          {address.inRotation ? t('wallets.table.inRotationYes') : t('wallets.table.inRotationNo')}
        </span>
      </td>
      <td className="px-3 py-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="size-tap text-muted-foreground sm:size-8"
              aria-label={t('wallets.actions.menu')}
              disabled={isActionPending}
            >
              <Settings2 className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onOpenControlMode} disabled={isControlModePending}>
              {t('wallets.actions.changeControlMode')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onToggleAml} disabled={isAmlPending}>
              <Shield className="mr-2 h-3.5 w-3.5" />
              {address.aml.monitoringEnabled
                ? t('wallets.actions.deactivateAml')
                : t('wallets.actions.activateAml')}
            </DropdownMenuItem>
            {address.aml.pdfUrl && (
              <DropdownMenuItem onClick={handleRiskClick}>
                <FileDown className="mr-2 h-3.5 w-3.5" />
                {t('wallets.actions.downloadPdf')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}
